import {computed, readonly, ref} from "vue";
import ApiService from "@/core/services/ApiService";

/**
 * The SUBSCRIPTION state of this device: what `reconcile` / `activate` /
 * `disable` decide. `error` is an operation that failed (activation, the
 * service worker), never the capability probe.
 */
type PushState = "off" | "pending" | "active" | "denied" | "error";

/**
 * X17 — whether this browser, on this backend, can do Web Push AT ALL: what
 * `prepareWebPush()` decides, and nothing else writes it. Orthogonal to
 * `PushState` — before X17 both lived in one ref, so the `reconcile` that
 * always follows the probe overwrote `error` / `unsupported` / `need-install`
 * with `off` and the switch became interactive on a failed probe.
 *
 *  - `checking`     the probe is in flight;
 *  - `unsupported`  no secure context / service worker / PushManager / Notification;
 *  - `need-install` iOS outside the Home Screen;
 *  - `unavailable`  the backend said `enabled=false` or has no public key — a
 *                   server decision, not an error, so no Retry;
 *  - `error`        the config request failed (network, 5xx, timeout) — Retry;
 *  - `ready`        enabled with a key: the switch may be turned on.
 */
export type WebPushCapability = "checking" | "unsupported" | "need-install" | "unavailable" | "error" | "ready";

/** Operation notices, as CODES — `@/i18n/webPush` turns them into RO/RU/EN. */
export type WebPushNotice = "wait_check" | "activate_failed" | "worker_unavailable" | "disable_pending";

type StoredRegistration = {registrationId: string; revokeToken: string; ownerId: number; state: PushState};
type PendingRevoke = Pick<StoredRegistration, "registrationId" | "revokeToken">;

const REGISTRATION_KEY = "partner_web_push_registration";
const PENDING_PREFIX = "partner_web_push_pending_revoke:";
const state = ref<PushState>("off");
const capability = ref<WebPushCapability>("checking");
const notice = ref<WebPushNotice | null>(null);
const ready = ref(false);
let cachedConfig: {enabled: boolean; public_key: string | null} | null = null;
let preparePromise: Promise<void> | null = null;
let operationVersion = 0;

const bytesToBase64Url = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes))
  .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const randomToken = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return bytesToBase64Url(bytes);
};

const uuid = () => (crypto as Crypto & {randomUUID?: () => string}).randomUUID
  ? (crypto as Crypto & {randomUUID: () => string}).randomUUID()
  : `${Date.now().toString(16)}-${randomToken().slice(0, 24)}`;

const readRegistration = (): StoredRegistration | null => {
  try { return JSON.parse(localStorage.getItem(REGISTRATION_KEY) || "null"); } catch (_) { return null; }
};
const saveRegistration = (value: StoredRegistration | null) => value
  ? localStorage.setItem(REGISTRATION_KEY, JSON.stringify(value))
  : localStorage.removeItem(REGISTRATION_KEY);
const readPending = (): PendingRevoke[] => {
  const items: PendingRevoke[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (!key?.startsWith(PENDING_PREFIX)) continue;
    try { items.push(JSON.parse(localStorage.getItem(key) || "null")); } catch (_) { /* keep corrupt key for manual retry */ }
  }
  return items.filter(Boolean);
};
const addPending = (item: PendingRevoke) => localStorage.setItem(`${PENDING_PREFIX}${item.registrationId}`, JSON.stringify(item));
const removePending = (registrationId: string) => localStorage.removeItem(`${PENDING_PREFIX}${registrationId}`);

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches
  || ("standalone" in navigator && (navigator as Navigator & {standalone?: boolean}).standalone === true);
const supported = () => window.isSecureContext && "serviceWorker" in navigator
  && "PushManager" in window && "Notification" in window;

function publicKeyBytes(value: string): Uint8Array {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  return Uint8Array.from(atob((value + padding).replace(/-/g, "+").replace(/_/g, "/")), char => char.charCodeAt(0));
}

async function revokeRemote(item: PendingRevoke): Promise<boolean> {
  try {
    await ApiService.post("/web-push/revoke", {registration_id: item.registrationId, revoke_token: item.revokeToken});
    return true;
  } catch (_) { return false; }
}

export async function flushPendingWebPushRevokes(): Promise<void> {
  for (const item of readPending()) {
    if (await revokeRemote(item)) removePending(item.registrationId);
  }
}

/**
 * The capability probe. Writes `capability` (and `ready`) ONLY — the
 * subscription `state` is `reconcile`'s. A failed probe is forgotten, so the
 * next call (Retry, or the `online` listener below) asks again; a successful
 * one is cached for the page's lifetime.
 */
export function prepareWebPush(): Promise<void> {
  if (preparePromise) return preparePromise;
  ready.value = false;
  capability.value = "checking";
  let failed = false;
  preparePromise = (async () => {
    if (!supported()) { capability.value = "unsupported"; ready.value = true; return; }
    if (isIos() && !isStandalone()) { capability.value = "need-install"; ready.value = true; return; }
    try {
      const {data} = await ApiService.get("/web-push/config");
      cachedConfig = {enabled: !!data.enabled, public_key: data.public_key || null};
      capability.value = cachedConfig.enabled && cachedConfig.public_key ? "ready" : "unavailable";
      if (notice.value === "wait_check") notice.value = null;
    } catch (_) {
      failed = true;
      cachedConfig = null;
      capability.value = "error";
    } finally {
      ready.value = true;
      if (failed) preparePromise = null;
    }
  })();
  return preparePromise;
}

export async function disableWebPush(): Promise<boolean> {
  const version = ++operationVersion;
  const current = readRegistration();
  if (current) {
    addPending({registrationId: current.registrationId, revokeToken: current.revokeToken});
    saveRegistration(null);
  }
  state.value = "pending";
  const stillOwnsCleanup = () => {
    if (version !== operationVersion) return false;
    const latest = readRegistration();
    return !latest || latest.registrationId === current?.registrationId;
  };
  const localUnsubscribe = supported()
    ? (async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration || !stillOwnsCleanup()) return false;
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription || !stillOwnsCleanup()) return false;
        return subscription.unsubscribe();
      } catch (_) { return false; }
    })()
    : Promise.resolve(false);
  await Promise.allSettled([current ? flushPendingWebPushRevokes() : Promise.resolve(), localUnsubscribe]);
  if (version !== operationVersion) return false;
  const complete = readPending().length === 0;
  state.value = "off";
  notice.value = complete ? null : "disable_pending";
  return complete;
}

export async function activateWebPush(ownerId: number): Promise<void> {
  const version = ++operationVersion;
  notice.value = null;
  // Nothing is prompted and nothing is posted unless the probe said yes. The
  // switch is not offered in any other capability, so reaching this is a race
  // (a click landing while a Retry is in flight) — answered, not acted on.
  if (capability.value !== "ready" || !cachedConfig?.enabled || !cachedConfig.public_key) {
    notice.value = capability.value === "checking" || capability.value === "error" ? "wait_check" : null;
    return;
  }
  state.value = "pending";
  // This must remain the first asynchronous browser call after the user's click (iOS requirement).
  const permission = await Notification.requestPermission();
  if (version !== operationVersion) return;
  if (permission !== "granted") { state.value = permission === "denied" ? "denied" : "off"; return; }

  try {
    await flushPendingWebPushRevokes();
    if (version !== operationVersion) return;
    const previous = readRegistration();
    if (previous && previous.ownerId !== ownerId) {
      await disableWebPush();
      return;
    }
    const worker = await navigator.serviceWorker.getRegistration();
    if (version !== operationVersion) return;
    if (!worker) throw new Error("service-worker-unavailable");
    const existing = await worker.pushManager.getSubscription();
    if (version !== operationVersion) return;
    const subscription = existing || await worker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: publicKeyBytes(cachedConfig.public_key),
    });
    if (version !== operationVersion) { await subscription.unsubscribe(); return; }
    const saved = readRegistration();
    const record: StoredRegistration = saved?.ownerId === ownerId
      ? {...saved, state: "pending"}
      : {registrationId: uuid(), revokeToken: randomToken(), ownerId, state: "pending"};
    saveRegistration(record);
    await ApiService.put(`/web-push/subscriptions/${record.registrationId}`, {
      revoke_token: record.revokeToken,
      subscription: subscription.toJSON(),
    });
    if (version !== operationVersion || readRegistration()?.ownerId !== ownerId) return;
    saveRegistration({...record, state: "active"});
    state.value = "active";
  } catch (_) {
    if (version === operationVersion) { state.value = "error"; notice.value = "activate_failed"; }
  }
}

export async function reconcileWebPush(ownerId?: number): Promise<void> {
  const version = ++operationVersion;
  await flushPendingWebPushRevokes();
  if (version !== operationVersion) return;
  const current = readRegistration();
  if (!ownerId) { state.value = current?.state || "off"; return; }
  if (current && current.ownerId !== ownerId) {
    if (version === operationVersion && readRegistration()?.registrationId === current.registrationId) await disableWebPush();
    return;
  }
  if (!current) { state.value = "off"; return; }
  if (!supported() || Notification.permission !== "granted") {
    if (version === operationVersion && readRegistration()?.registrationId === current.registrationId) await disableWebPush();
    return;
  }
  const worker = await navigator.serviceWorker.getRegistration();
  if (version !== operationVersion) return;
  if (!worker) { state.value = "error"; notice.value = "worker_unavailable"; return; }
  const subscription = await worker.pushManager.getSubscription();
  if (version !== operationVersion) return;
  if (!subscription) {
    if (readRegistration()?.registrationId === current.registrationId) await disableWebPush();
    return;
  }
  try {
    const {data} = await ApiService.get(`/web-push/subscriptions/${current.registrationId}`);
    if (version !== operationVersion || readRegistration()?.registrationId !== current.registrationId) return;
    state.value = data.enabled ? "active" : "off";
  } catch (_) { if (version === operationVersion) state.value = "off"; }
}

export function invalidateWebPushOperations(): void { operationVersion++; }
export function webPushIdentityConfirmed(ownerId: number): void { void prepareWebPush().then(() => reconcileWebPush(ownerId)); }
export function webPushExplicitLogout(): void { invalidateWebPushOperations(); void disableWebPush(); }
export function webPushIdentityChanging(): void { invalidateWebPushOperations(); void disableWebPush(); }

export async function acknowledgeVisiblePush(eventId: string, ownerId: number): Promise<boolean> {
  if (document.visibilityState !== "visible" || !/^[a-f0-9]{64}$/i.test(eventId)) return false;
  const version = operationVersion;
  const current = readRegistration();
  if (!current || current.state !== "active" || current.ownerId !== ownerId) return false;
  if (version !== operationVersion || readRegistration()?.registrationId !== current.registrationId) return false;
  try {
    const {data} = await ApiService.post("/web-push/ack", {
      registration_id: current.registrationId,
      event_id: eventId,
    });
    return version === operationVersion
      && readRegistration()?.registrationId === current.registrationId
      && data.acknowledged === true;
  } catch (_) { return false; }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    void flushPendingWebPushRevokes();
    if (!cachedConfig) void prepareWebPush();
  });
  window.addEventListener("storage", event => {
    if (event.key === REGISTRATION_KEY || event.key?.startsWith(PENDING_PREFIX)) {
      operationVersion++;
      const current = readRegistration();
      state.value = current?.state || "off";
    }
  });
}

export function useWebPush() {
  return {
    state: readonly(state),
    capability: readonly(capability),
    notice: readonly(notice),
    ready: readonly(ready),
    active: computed(() => state.value === "active"),
    activate: activateWebPush,
    disable: disableWebPush,
    reconcile: reconcileWebPush,
    prepare: prepareWebPush,
  };
}
